import { useRef, useEffect } from "react";
import { saveAs } from 'file-saver';
import {
	PREMIUM_INDEX,
	PREMIUM_PRO_INDEX,
} from '../constants/permissionConstants';
import { format, parseISO, intervalToDuration } from 'date-fns';
import {decryptData} from './crypto'
import { getCookie } from "./cookieHelper";

const callTracker = new Map();

/**
 * Function to calculate and format the file size
 *
 * @param {string} bytes - Size of file.
 * @return {string} - returns a human-readable format (KB, MB, etc.).
 */
export const formatFileSize = (bytes) => {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const convertKBytesToMBytes = (Kbytes) => {
	if (Kbytes === 0) {
		return 0;
	}
	return (Kbytes / 1024).toFixed(2);
};

export const convertBytesToMbOrGb = (bytes, targetUnit) => {
	const units = {
		KB: 1024, // 1 KB = 1024 bytes
		MB: 1024 * 1024, // 1 MB = 1024 * 1024 bytes
		GB: 1024 * 1024 * 1024, // 1 GB = 1024 * 1024 * 1024 bytes
	};

	if (!units[targetUnit]) {
		throw new Error("Invalid target unit. Please use 'KB', 'MB' or 'GB'.");
	}

	const convertedValue = (bytes / units[targetUnit]).toFixed(2); // Convert and round to 2 decimal places
	return `${convertedValue}`;
};

/**
 * Function to format the date
 *
 * @param {string} dateString - Date value. ("2024-07-22T14:19:27.326525")
 * @return {string} - returns a readable format .
 */
export const formatDate = (dateString) => {
	// Convert the string into a Date object
	const date = new Date(dateString);

	// Format the date into a readable format
	const formattedDate = date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});

	const formattedTime = date.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});

	// Combine date and time
	const representableDate = `${formattedDate} ${formattedTime}`;

	return representableDate;
};

/**
 * Background color for graph
 */
export const backgroundColor = [
	'#876cdd',
	'#d0b1f2',
	'#621c8a',
	'#e4cadd',
	'#523e8f',
	'#a79cc7',
	'#9648c2',
	'#007b83',
	'#d01884',
	'#d93025',
	'#a142f4',
	'#1a73e8',
	'#f9ab00',
	'#188038',
	'#290040',
	'#390357',
	'#ffecc2',
	'#fc9d03',
	'#7d4705',
	'#54330d',
	'#befade',
	'#00cf64',
	'#00ab50',
	'#03612c',
	'#08421f',
];
export const mcsBackgroundColor = [
	'#188038',
	'#500d76',
	'#9648c2',
	'#f9ab00',
	'#523e8f',
];

/*
 * make odd and even array from array of object
 * @param {*} ports
 */

export const oddEvenObject = (ports) => {
	const oddPorts = [];
	const evenPorts = [];

	// Single loop to divide into odd and even
	ports.forEach((port) => {
		if (port.port_no % 2 !== 0) {
			oddPorts.push(port); // Odd port_no
		} else {
			evenPorts.push(port); // Even port_no
		}
	});
	return { oddPorts, evenPorts };
};

export const calculateDaysBetweenDates = (currentDate, futureDate) => {
	// Convert the date strings to Date objects
	const firstDate = new Date(currentDate);
	const secondDate = new Date(futureDate);

	// If the future date is before the current date, return 0
	if (secondDate < firstDate) return 0;

	// Calculate the difference in time (milliseconds)
	const timeDifference = Math.abs(secondDate - firstDate);

	// Convert milliseconds to days (1 day = 24 hours * 60 minutes * 60 seconds * 1000 milliseconds)
	const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

	return daysDifference;
};

// expect date string in 12/09/2024 10:00AM format and returns epoch
export const convertDatetoEpochString = (dateTimeString) => {
	// Split the date and time parts
	console.log('conversion : ', dateTimeString);
	const [datePart, timePart] = dateTimeString?.split(' ');

	// Split the day, month, and year
	const [day, month, year] = datePart
		?.split('/')
		?.map((part) => parseInt(part, 10));

	// Extract hours, minutes, and the AM/PM part
	const [time, period] = [timePart?.slice(0, -2), timePart?.slice(-2)];
	let [hours, minutes] = time?.split(':')?.map((part) => parseInt(part, 10));

	// Convert 12-hour format to 24-hour format
	if (period === 'PM' && hours !== 12) {
		hours += 12;
	} else if (period === 'AM' && hours === 12) {
		hours = 0;
	}

	// Create a Date object
	const date = new Date(year, month - 1, day, hours, minutes, 0, 0); // month - 1 because months are 0-indexed

	// Convert to epoch time (in milliseconds)
	const epochTime = date.getTime();
	console.log('conversion 111: ', epochTime);
	return epochTime;
};

const getCookieVal = (name) => {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		const encryptedValue = parts?.pop()?.split(';')?.shift();
		try {
			const decryptedValue = decryptData(encryptedValue);
			return decryptedValue; // Timezone is stored as a string, not JSON
		} catch (error) {
			console.error('Error decrypting cookie:', error);
			return null;
		}
	}
	return null;
};

export const convertDatetoEpochStringUTC = (dateTimeString) => {
	const timezone = getCookieVal('timezone') || 'UTC';

	const [datePart, timePart] = dateTimeString.split(' ');
	const [day, month, year] = datePart.split('/').map(Number);

	const time = timePart.slice(0, -2);
	const period = timePart.slice(-2);
	let [hours, minutes] = time.split(':').map(Number);

	if (period === 'PM' && hours !== 12) hours += 12;
	if (period === 'AM' && hours === 12) hours = 0;

	const guessUTC = Date.UTC(year, month - 1, day, hours, minutes, 0);

	const offsetMs = getTimeZoneOffset(timezone, guessUTC);

	return guessUTC - offsetMs;
};

const getTimeZoneOffset = (timeZone, utcMillis) => {
	const formatter = new Intl.DateTimeFormat('en-US', {
		timeZone,
		hour12: false,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});

	const parts = formatter.formatToParts(new Date(utcMillis));
	const values = Object.fromEntries(
		parts.map((p) => [p.type, Number(p.value)])
	);

	const asUTC = Date.UTC(
		values.year,
		values.month - 1,
		values.day,
		values.hour,
		values.minute,
		values.second
	);

	return asUTC - utcMillis;
};


export const convertUTCtoFormattedDate = (epochTime) => {
	// Auto-detect if the input is in seconds (10-digit) or milliseconds (13-digit)
	const epochInMs = epochTime < 1e12 ? epochTime * 1000 : epochTime;

	const date = new Date(epochInMs); // Create Date from milliseconds

	// Extract UTC parts
	const day = String(date.getUTCDate()).padStart(2, '0');
	const monthNames = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	];
	const month = monthNames[date.getUTCMonth()];
	const year = date.getUTCFullYear();

	const hours = String(date.getUTCHours()).padStart(2, '0');
	const minutes = String(date.getUTCMinutes()).padStart(2, '0');

	return `${day}-${month}-${year}, ${hours}:${minutes}`;
};

export const convertEpochToDateString = (epochTime) => {
	// Create a Date object from the epoch time
	const date = new Date(epochTime);

	// Extract day, month, year, hours, and minutes
	const day = date.getDate();
	const month = date.getMonth() + 1; // months are 0-indexed
	const year = date.getFullYear();
	let hours = date.getHours();
	const minutes = date.getMinutes();

	// Determine AM/PM
	const period = hours >= 12 ? 'PM' : 'AM';

	// Convert hours from 24-hour to 12-hour format
	hours = hours % 12;
	hours = hours ? hours : 12; // if hour is 0, set it to 12

	// Format the date and time parts
	const datePart = `${String(day).padStart(2, '0')}/${String(month).padStart(
		2,
		'0'
	)}/${year}`;
	const timePart = `${String(hours).padStart(2, '0')}:${String(
		minutes
	).padStart(2, '0')}${period}`;

	// Combine date and time parts
	const dateTimeString = `${datePart} ${timePart}`;

	return dateTimeString;
};
/**
 * @param  {string} epochString  Accepts epoch string
 * @param  {string} timeZone  Accepts time zone
 * @return {string} dd-mmm-yyyy, hh:mm (24 hours) format
 */
export function formatEpoch(
	epochString,
	timeZone = getCookieVal?.('timezone') || 'UTC'
) {
	const epoch = Number(epochString);
	const date = new Date(epoch < 1e12 ? epoch * 1000 : epoch);

	const formatter = new Intl.DateTimeFormat('en-GB', {
		timeZone,
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	});

	const parts = formatter.formatToParts(date);
	const get = (type) => parts.find(p => p.type === type)?.value;

	return `${get('day')}-${get('month')}-${get('year')}, ` +
	       `${get('hour')}:${get('minute')}:${get('second')}`;
}



/**
 * @param  {string} dateString  Expecting "DD-MM-YYYY HH:mm:ss"
 * @param  {boolean} includeSeconds
 * @return {string} e.g. "15-May-2026, 10:55:33"
 */
export function formatDateTime(dateString, includeSeconds = true) {
	if (!dateString) return '';

	// Expecting: "DD-MM-YYYY HH:mm:ss"
	const [datePart, timePart] = dateString.split(' ');
	const [day, month, year] = datePart.split('-');
	const [hour, minute, second] = timePart.split(':');

	const months = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	];

	const formattedDate = `${day}-${months[parseInt(month, 10) - 1]}-${year}`;
	const formattedTime =
		includeSeconds && second !== undefined
			? `${hour}:${minute}:${second}`
			: `${hour}:${minute}`;

	return `${formattedDate}, ${formattedTime}`;
}

/**
 *
 * @param {*} filterBy  : 4h|24h|1w|custom
 * @param {*} from :when pass custom date -> date string in 12/09/2024 10:00AM format
 * @param {*} to :when pass custom date -> date string in 12/09/2024 10:00AM format
 */

export const getStartEndEpochStringByFilter = (filterBy = '4h', from, to) => {
	const currentEpochTime = Date.now();
	switch (filterBy) {
		case '4h': {
			const secondsInFourHours = 4 * 60 * 60 * 1000;
			const fourHoursAgoEpochTime = currentEpochTime - secondsInFourHours;
			return {
				startTime: fourHoursAgoEpochTime,
				endTime: currentEpochTime,
			};
		}
		case '24h': {
			const tz = decryptData(getCookie('timezone')) || 'Asia/Kolkata'; // or pass param
			const now = Date.now(); // UTC epoch
			const startTime = midnightTodayInTZ(tz, now); // UTC epoch for 00:00 in tz
			// const secondsInTwentyFourHours = 24 * 60 * 60 * 1000;
			// const twentyfourHoursAgoEpochTime =
			// 	currentEpochTime - secondsInTwentyFourHours;
			return {
				startTime: startTime,
				endTime: now,
			};
		}
		case '1w': {
			const secondsInWeek = 24 * 7 * 60 * 60 * 1000;
			const weekAgoEpochTime = currentEpochTime - secondsInWeek;
			return {
				startTime: weekAgoEpochTime,
				endTime: currentEpochTime,
			};
		}
		case '1m': {
			const secondsInWeek = 24 * 30 * 60 * 60 * 1000;
			const weekAgoEpochTime = currentEpochTime - secondsInWeek;
			return {
				startTime: weekAgoEpochTime,
				endTime: currentEpochTime,
			};
		}
		case 'custom': {
			if (!from || !to) {
				return {
					startTime: '',
					endTime: '',
				};
			}
			return {
				startTime: convertDatetoEpochString(from),
				endTime: convertDatetoEpochString(to),
			};
		}
		default: {
			const secondsInFourHours = 4 * 60 * 60 * 1000;
			const fourHoursAgoEpochTime = currentEpochTime - secondsInFourHours;
			return {
				startTime: fourHoursAgoEpochTime,
				endTime: currentEpochTime,
			};
		}
	}
};

// helper: get offset (ms) of given timeZone at time t
const offsetMsAt = (timeZone, t = Date.now()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(t)).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});

  const wallUTC = Date.UTC(
    +parts.year,
    +parts.month - 1,
    +parts.day,
    +parts.hour,
    +parts.minute,
    +parts.second,
  );
  return wallUTC - t;
};

// helper: midnight in given timezone, returned as UTC epoch
const midnightTodayInTZ = (timeZone, t = Date.now()) => {
  const wall = new Date(t + offsetMsAt(timeZone, t)); // shift to tz wall clock
  const y = wall.getUTCFullYear();
  const m = wall.getUTCMonth();
  const d = wall.getUTCDate();
  const wallMidUTC = Date.UTC(y, m, d, 0, 0, 0, 0);
  return wallMidUTC - offsetMsAt(timeZone, wallMidUTC);
};

export const getFirstString = (value) => {
	// Check if the string contains a pipe
	if (typeof value === 'string' && value?.includes('|')) {
		// If pipe exists, split and return the first part
		return value?.split('|')[0];
	}
	// If no pipe, return the original string
	return value;
};

export const getSiteIdFromSiteName = (siteList = [], siteName = null) => {
	// Check if the siteName and siteList
	if (siteList.length !== 0 && siteName && siteName !== null) {
		return siteList.find((obj) => obj.siteName === siteName);
	} else {
		return null;
	}
};
// utils/dateFormatter.js
export const formatDateWithTimezone = (date, includeSeconds = false) => {
	if (!date || date === '-') return ' ';

	const dateObj = new Date(date);

	// Check for invalid date
	if (isNaN(dateObj.getTime())) return ' ';

	const day = String(dateObj.getDate()).padStart(2, '0'); // Add leading zero to day
	const month = dateObj.toLocaleString('en-GB', { month: 'short' }); // Short month format (e.g., Jan, Feb)
	const year = dateObj.getFullYear();
	const hours = String(dateObj.getHours()).padStart(2, '0'); // 24-hour format with leading zero
	const minutes = String(dateObj.getMinutes()).padStart(2, '0'); // Add leading zero to minutes
   const seconds = String(dateObj.getSeconds()).padStart(2, '0');

	return includeSeconds
		? `${day}-${month}-${year}, ${hours}:${minutes}:${seconds}`
		: `${day}-${month}-${year}, ${hours}:${minutes}`;
};


export const convertToDDMMYYYY = (dateString) => {
	// Split the date and time parts
	const dateTimeParts = dateString?.split(' ');
	const dateParts = dateTimeParts[0]?.split('-');

	// Check if the date is in the format "DD-MM-YYYY"
	if (
		dateParts.length === 3 &&
		dateParts[0].length === 2 &&
		dateParts[1].length === 2 &&
		dateParts[2].length === 4
	) {
		const day = dateParts[0];
		const month = dateParts[1];
		const year = dateParts[2];

		// Reformat the date to "YYYY-MM-DD"
		const formattedDate = `${year}-${month}-${day}`;
		// Return the newly formatted date with the original time part
		return `${formattedDate} ${dateTimeParts[1]}`;
	}
	// If the date is already in "YYYY-MM-DD" format, return it as-is
	return dateString;
};

/**
 * modelCounts = {
    ion4xe: {
      "1.2.1": 1,
      "1.2.2.1": 2,
      "3.2.2.1": 3,
    },
    ion4xi: {
      "1.2.1": 1,
      "1.2.2.1": 3,
      "6.2.5.1": 5,
      "8.2.8.1": 6,
    },
	Additional models can be added here, like "ion4xz" or others.
  };
 * @param {*} modelCounts 
*/

export const generateData = (modelCounts = {}) => {
	// Step 1: Get all unique version labels across all models
	const versions = Array.from(
		new Set(
			Object.values(modelCounts).flatMap((modelData) =>
				Object.keys(modelData)
			)
		)
	);
	// Step 2: Get all model labels (ion4xe, ion4xi, ...)
	const modelLabels = Object.keys(modelCounts).sort((a, b) => {
		if (a === 'Unknown') return 1; // Place "Unknown" last
		if (b === 'Unknown') return -1; // Place "Unknown" last
		return a.localeCompare(b); // Sort others alphabetically
	});
	// Step 3: Create datasets for each version (stacked data)

	return {
		datasets: versions.map((version, index) => ({
			label: version,
			data: modelLabels.map((model) => modelCounts[model][version] || 0),
			backgroundColor: backgroundColor[index % backgroundColor.length],
			maxBarThickness: 10,
		})),
		label: modelLabels.map((label) => `AP Model: ${label}`),
	};
};

// format date to 'hh:mm'
export const formatTimeToHHMM = (timeString) => {
	if (!timeString) return ''; // Handle if timeString is undefined or null

	// Extract hours and minutes from the time string (HH:mm:ss)
	const [hours, minutes] = timeString?.split(':');
	return `${hours}:${minutes}`; // Return formatted string in HH:mm
};

export const shouldShowSignup = (url) => {
	try {
		const hostname = new URL(url).hostname;
		const domain = hostname?.split('.').slice(-2).join('.');

		return domain === 'ztp';
	} catch (error) {
		return false;
	}
};

// accepts time in seconds and return time formatted in minutes, hours , so on.
export const formatTime = (value) => {
	if (value === null || value === undefined || value === '') {
	    return '--';
	}

  	let seconds = Number(value);

	if (!Number.isFinite(seconds) || seconds < 0) {
		return '-';
	}

	const days = Math.floor(seconds / (24 * 3600));
	seconds %= 24 * 3600;

	const hours = Math.floor(seconds / 3600);
	seconds %= 3600;

	const minutes = Math.floor(seconds / 60);

	return `${days}d ${hours}h ${minutes}m`;
};

export const fetchPermission = (name, permissionType = null) => {
	const permissionName = `${permissionType}_${name}`;
	if (permissionType) {
		const rolePermission = JSON.parse(
			decryptData(localStorage.getItem('rolepermission'))
		);
		const matchedPermission = rolePermission?.find(
			(val) => val.codename === permissionName
		);
		if (matchedPermission) {
			if (matchedPermission?.feature === 'switch' || matchedPermission?.feature === 'license') {
				return true;
			}
			return matchedPermission?.wifi ? true : false;
		}
		return false;
	} else {
		const permission = 	decryptData(localStorage.getItem('permission'))
			? JSON.parse(decryptData(localStorage.getItem('permission')))
			: '';
		return permission?.includes(name);
	}
};

export const fetchFeaturesPermission = (featureName) => {
  const featuresData = decryptData(localStorage.getItem("featuresAllowed"));
   if (!featuresData || featuresData === "undefined") {
    return false;
  }

  const features = JSON.parse(featuresData) || {};
  return !!features[featureName]; 
};

export const getErrorMsgFromApiResp = (error) => {
	let errorMessage = 'An error occurred';

	if (Array.isArray(error)) {
		if (typeof error[0] === 'object') {
			errorMessage = Object.values(error[0])[0] || errorMessage;
		} else {
			errorMessage = error[0] || errorMessage;
		}
	} else if (typeof error === 'string') {
		errorMessage = error;
	} else if (typeof error === 'object') {
		errorMessage = Object.values(error)[0];
	} else if (error.nested_site_name) {
		return (errorMessage = error.nested_site_name);
	} else if (error.non_field_errors) {
		return (errorMessage = Array.isArray(error.non_field_errors)
			? error.non_field_errors[0]
			: error.non_field_errors);
	}
	return errorMessage;
};

export const downloadResponseInCsv = (res, filename) => {
	const mediaType = 'application/octet-stream';
	const blob = new Blob([res], { type: mediaType });

	const newDate = new Date();
	const date = ('0' + newDate.getDate()).slice(-2);
	const month = ('0' + (newDate.getMonth() + 1)).slice(-2);
	const yr = newDate.getFullYear().toString().substr(-2);
	const hh = ('0' + newDate.getHours()).slice(-2).toString();
	const mm = ('0' + newDate.getMinutes()).slice(-2).toString();

	filename =
		filename +
		'_' +
		date +
		'-' +
		month +
		'-' +
		yr +
		'_' +
		hh +
		'-' +
		mm +
		'.gz.tar';
	return saveAs(blob, filename);
	// this.loading = false;
};

export const formatToMacAddress = (input) => {
	// Remove any non-hexadecimal characters
	const cleanInput = input.replace(/[^a-fA-F0-9]/g, '');

	// Insert a colon every two characters, but only if at least 2 chars exist in each group
	return (
		cleanInput
			.match(/.{1,2}/g) // Break into groups of 1 or 2 characters
			?.join(':') // Join with colons
			.substring(0, 17) || ''
	); // Limit to 17 characters (max MAC address length)
};

export const validateVlanId = (vlanId) => {
	const VALID_MIN = 2;
	const VALID_MAX = 4094;

	// Check if the VLAN ID is a number and within the valid range
	const num = parseInt(vlanId, 10);
	return !isNaN(num) && num >= VALID_MIN && num <= VALID_MAX;
};

export const getDevicePermission = (
	licenseType,
	permission,
	deviceProPermissions,
	devicePrePermissions
) => {
	// Check if the device has a PREMIUM license
	if (licenseType?.[PREMIUM_INDEX] === 1) {
		if (devicePrePermissions.includes(permission)) {
			return true; // Permission exists for PREMIUM license
		}
	}

	// Check if the device has a PREMIUM PRO license
	if (licenseType?.[PREMIUM_PRO_INDEX] === 1) {
		if (deviceProPermissions.includes(permission)) {
			return true; // Permission exists for PREMIUM PRO license
		}
	}

	// Return false if no permission matches
	return false;
};

export const formatUptime = (uptime) => {
	const hoursArray = uptime?.split(':') || []; // Split HH:MM:SS into an array

	const totalHours = parseInt(hoursArray[0], 10) || 0; // Extract and parse hours
	const minutes = parseInt(hoursArray[1], 10) || 0; // Extract minutes

	// Calculate days and remaining hours
	const days = Math.floor(totalHours / 24);
	const remainingHours = totalHours % 24;

	return `${days}d ${remainingHours}h ${minutes}m`;
};

export const getLabel = (label) => {
	switch (label) {
		case 'lte_4hours': {
			return '< 4hours';
		}
		case 'gte_4hours': {
			return '> 4hours';
		}
		case 'lte_week': {
			return '< Week';
		}
		case 'gte_week': {
			return '> Week';
		}
		case 'lte_today': {
			return '< Day';
		}
		case 'gte_today': {
			return '> Day';
		}
		case 'lte_month': {
			return '< Month';
		}
		case 'gte_month': {
			return '> Month';
		}
		default: {
			return label;
		}
	}
};

export const filterValue = {
	'4h': '4hours',
	'24h': 'today',
	'1w': '7days',
	'1m': '30days',
	custom: 'custom',
};

export const convertEpochToTimeStamp = (epochTime) => {
	// Auto-detect if epoch is in seconds or milliseconds
  const ms = epochTime < 1e12 ? epochTime * 1000 : epochTime;
  return new Date(ms).toISOString();
}
export const convertToTime = (epochTime) =>
	format(new Date(epochTime * 1000), 'HH:mm');
/**
 * Generates a random alphanumeric string of a specified length.
 * @param {number} length - The desired length of the random string.
 * @return {string} - The generated random alphanumeric string.
 */
export function generateRandomAlphanumericString(length) {
	const characters =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let result = '';
	for (let i = 0; i < length; i++) {
		const randomIndex = Math.floor(Math.random() * characters.length);
		result += characters[randomIndex];
	}
	return result;
}

export const fillMissingTimestamps = (data, intervalMinutes) => {
	const filledData = [];
	const intervalMillis = intervalMinutes * 60 * 1000; // Convert minutes to milliseconds

	for (let i = 0; i < data.length - 1; i++) {
		filledData.push(data[i]);

		let currentTimestamp = new Date(data[i].statsTime).getTime();
		const nextTimestamp = new Date(data[i + 1].statsTime).getTime();

		// Insert missing data points
		while (currentTimestamp + intervalMillis < nextTimestamp) {
			currentTimestamp += intervalMillis;
			filledData.push({
				statsTime: new Date(currentTimestamp).toISOString(),
				txRate: null,
				rssi: null,
				txBytes: null,
				rxBytes: null,
				routerId: data[i].routerId,
				radioId: data[i].radioId,
				__typename: data[i].__typename,
			});
		}
	}

	// Add the last data point
	filledData.push(data[data.length - 1]);

	return filledData;
};

export const getErrorMsgFromApiRespRole = (error) => {
	let errorMessage = 'An error occurred';
	if (Array.isArray(error)) {
		errorMessage = error[0] || errorMessage;
	} else if (typeof error === 'string') {
		errorMessage = error;
	} else if (typeof error === 'object') {
		if (
			error.CANNOT_DELETE_ASSIGNED_ROLE &&
			Array.isArray(error.CANNOT_DELETE_ASSIGNED_ROLE)
		) {
			errorMessage = 'CANNOT_DELETE_ASSIGNED_ROLE';
		} else if (error.msg) {
			errorMessage = error.msg;
		}

		for (const key in error) {
			if (Array.isArray(error[key]) && error[key].length > 0) {
				// Ensure we return only string messages
				const firstValidMessage = key;
				if (firstValidMessage) {
					return firstValidMessage;
				}
			}
		}
	}
	return errorMessage;
};

export const hasObjectChanged = (initialValue, updatedValue) => {
	// If both are primitives, compare directly
	if (
		typeof initialValue !== 'object' ||
		typeof updatedValue !== 'object' ||
		initialValue === null ||
		updatedValue === null
	) {
		return initialValue !== updatedValue;
	}

	// If both are arrays, compare lengths and elements recursively
	if (Array.isArray(initialValue) && Array.isArray(updatedValue)) {
		if (initialValue.length !== updatedValue.length) return true;
		return initialValue.some((item, index) =>
			hasObjectChanged(item, updatedValue[index])
		);
	}

	// If both are objects, compare keys and values recursively
	const initialKeys = Object.keys(initialValue);
	// const updatedKeys = Object.keys(updatedValue);

	for (const key of initialKeys) {
		if (hasObjectChanged(initialValue[key], updatedValue[key])) {
			return true;
		}
	}
	return false;
};

export const generateKeyArrays = (data) => {
	// empty / invalid data
    if (!Array.isArray(data) || data.length === 0) {
        return { unit: '' };
    }
 
    // Collect ALL keys that appear in any object
    const keys = Array.from(
        new Set(
            data.reduce((acc, item) => {
                if (item && typeof item === 'object') {
                    return acc.concat(Object.keys(item));
                }
                return acc;
            }, [])
        )
    );
	const result = {
		unit: '',
		txRxUnit : 'Kbps'
	};

	keys.forEach((key) => {
		result[key] = data.map((item) => {
			if (
				key === 'phy_rate' &&
				typeof item[key] === 'string' &&
				item[key].includes('/')
			) {
				const splitRate = item[key]?.split(' ');
				// only first time
				if (result.unit.length === 0) {
					result.unit = splitRate[1];
				}
				return parseFloat(splitRate[0]);
			}
			// THROUGHPUT HANDLING (NEW + OLD)
			if (key === 'tx_throughput' || key === 'rx_throughput') {
				const value = item[key] ?? 0;

				// NEW API → already in Kbps
				if (item?.unit === 'Kbps') {
					return value;
				}

				// OLD API → Mbps → convert to Kbps
				return value * 1024;
			}

			if (key === 'rssi' && item[key] === 0) {
				return null;
			} else if (key === 'timestamp') {
				if (item[key]) {
					return convertEpochToTimeStamp(item[key]);
				} else {
					return null; // Or use a default value like Date.now()
				}
			} else return item[key];
		}); // Extract all values for each key
	});

	return result;
};

/**
 * air quality graph
 * @param {*} arr
 * @param {*} channel5Data
 */

export const map5Channel = (arr, channel5Data) => {
	for (let i = 0; i < arr.length; i++) {
		const type = /[^_]*$/.exec(arr[i].mode)[0];
		if (type === 'VHT20') {
			arr[i].channels =
				channel5Data['HT20'][arr[i].channel] || arr[i].channel;
		} else if (type === 'VHT40PLUS' || type === 'HT40PLUS') {
			arr[i].channels =
				channel5Data['HT40+'][arr[i].channel] || arr[i].channel;
		} else if (type === 'VHT40MINUS') {
			arr[i].channels =
				channel5Data['HT40-'][arr[i].channel] || arr[i].channel;
		}
		//   if (type === 'VHT80') {
		// 	arr[i].channels = channel5Data['HT80'][arr[i].channel] || arr[i].channel;
		//   } else if (type === 'VHT80PLUS80') {
		// 	arr[i].channels = channel5Data['HT80+80'][arr[i].channel] || arr[i].channel;
		//   } else if (type === 'VHT160') {
		// 	arr[i].channels = channel5Data['HT160'][arr[i].channel] || arr[i].channel;
		//   }
		else {
			arr[i].channels = arr[i].channel;
		}
	}
	return arr;
};

/**
 * air quality graph
 * @param {*} arr
 * @param {*} channel24Data
 */

export const map24Channel = (arr, channel24Data) => {
	for (let i = 0; i < arr.length; i++) {
		const type = /[^_]*$/.exec(arr[i].wirelessMode)[0];
		if (type === 'HT20') {
			arr[i].channels =
				channel24Data['HT20'][arr[i].channel] || arr[i].channel;
		} else if (type === 'HT40PLUS') {
			arr[i].channels =
				channel24Data['HT40+'][arr[i].channel] || arr[i].channel;
		} else if (type === 'HT40MINUS') {
			arr[i].channels =
				channel24Data['HT40-'][arr[i].channel] || arr[i].channel;
		} else {
			arr[i].channels = arr[i].channel;
		}
	}
	return arr;
};

/**
 *
 * @param {*} ssidList
 * @param {*} selectedTab
 */

export const prepareGraphData = (ssidList, selectedTab) => {
	return ssidList.map((ssid, index) => {
		const ssidname = ssid.ssid_name;
		const width = ssid.channel_width;
		const channel = ssid?.channels?.toString().split('+').map(Number);
		channel.sort((a, b) => a - b); // Sort channels in ascending order
		const channelWidth = width / 5;
		let startPoint = channel[0] - channelWidth / (2 * channel.length);
		let endPoint =
			channel[channel.length - 1] + channelWidth / (2 * channel.length);

		if (selectedTab === 1) {
			// 2.4 GHz
			startPoint = Math.max(startPoint, -1);
			endPoint = Math.min(endPoint, 15);
		} else {
			// 5 GHz
			startPoint = Math.max(startPoint, 25);
			endPoint = Math.min(endPoint, 175);
		}

		const rssi = ssid.rssi;
		const dataPoints = [
			{ x: startPoint, y: -100, width: width }, // Start of curve
			{
				x: channel[0],
				y: rssi,
				label: ssidname,
				dataLabels: { enabled: true },
				width: width,
			}, // Peak at first channel
		];

		// Loop through all channels and add peak RSSI values
		for (let i = 1; i < channel.length; i++) {
			dataPoints.push({
				x: channel[i],
				y: rssi,
				dataLabels: { enabled: false },
				width: width,
			});
		}

		dataPoints.push({ x: endPoint, y: -100, width: width }); // End of curve

		return {
			label: ssidname,
			data: dataPoints,
			borderColor: backgroundColor[index % backgroundColor.length],
			backgroundColor: backgroundColor[index % backgroundColor.length],
			tension: 0.5, // Smooth curves
			pointRadius: 0,
			borderWidth: 2,
			hitRadius: 10, // Increase hit area for tooltip detection
			hoverRadius: 6,
		};
	});
};

/**
 *
 * @param {*} epochTime
 * @return {*} yyyy-MM-dd HH:mm:ss
 */
export const formatDateTimeFromEpoch = (epochTime) => {
	const timestamp = epochTime; // Timestamp in milliseconds
	const formattedDate = format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
	return formattedDate;
};

/**
 *
 * @param {*} dateStr 2025-04-04T10:29:17.298Z
 * @return {*} yyyy-MM-dd HH:mm:ss
 */
export const formatDateString = (dateStr) => {
	if (dateStr) {
		return format(parseISO(dateStr), 'yyyy-MM-dd HH:mm:ss');
	} else {
		return '--';
	}
};

/**
 *
 * @param {*} durationMs 2025-04-04T10:29:17.298Z
 * @return {*} yyyy-MM-dd HH:mm:ss
 */
export const formatDuration = (durationMs) => {
	// handle <1s 
  	if (durationMs < 1000) return 'Less than 1 second';

	// round milliseconds into seconds
  	const roundedMs = Math.round(durationMs / 1000) * 1000;
	const duration = intervalToDuration({ start: 0, end: roundedMs });
	const parts = [];

	if (duration?.days){
		parts.push(`${duration.days}d`);
	}
	if (duration?.hours){
		parts.push(`${duration.hours}h`);
	}
	if (duration?.minutes){
		parts.push(`${duration.minutes}m`);
	}
	if (duration?.seconds){
		parts.push(`${duration.seconds}s`);
	}

	// if everything was zero, show "0s" instead of blank
	return parts?.length > 0 ? parts?.join(' ') : '--';
};

/**
 *
 * @param {*} dateStr 2025-04-04T10:29:17.298Z
 * @return {*} yyyy-MM-dd HH:mm:ss
 */
export const applyLocalOffset = (dateStr) => {
	const date = new Date(dateStr); // Already UTC input
	return format(date, 'yyyy-MM-dd HH:mm:ss'); // Formats using local time automatically
};

/**
 * Returns elapsed time since the given timestamp in "Xd Yh Zm" format.
 * @param {number} timestampSec – UNIX timestamp in seconds.
 * @return {string} e.g. "20d 7h 44m"
 */
export const timeSinceCompact = (timestampSec) => {
	const now = Date.now();
	const then = timestampSec * 1000;
	let diffMs = now - then;

	const msPerMinute = 60 * 1000;
	const msPerHour = 60 * msPerMinute;
	const msPerDay = 24 * msPerHour;

	const days = Math.floor(diffMs / msPerDay);
	diffMs -= days * msPerDay;

	const hours = Math.floor(diffMs / msPerHour);
	diffMs -= hours * msPerHour;

	const minutes = Math.floor(diffMs / msPerMinute);

	return `${days}d ${hours}h ${minutes}m`;
	
};
export const getUserLocale = () => {
	return navigator.language || "en-US";
};

export const timeSinceCompactSwitch = (timestampSec) => {
	if (!timestampSec) return "-";
  
	// Backend sends UTC epoch in seconds
	const date = new Date(timestampSec * 1000);
  
	return new Intl.DateTimeFormat(getUserLocale(), {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	})
		.format(date)
		.replace(",", "");
  };
  

// it will return highest data unit
export const formatBytesForYAxis = (dataList) => {
	const units = ['KB', 'MB', 'GB', 'TB'];
	const usageInKB = dataList.map(item => item.total_usage / 1024); // convert bytes to KB
	let unitIndex = 0;

	const maxVal = Math.max(...usageInKB);

	// Find appropriate unit based on max value
	while (
		maxVal / Math.pow(1024, unitIndex) >= 1024 &&
		unitIndex < units.length - 1
	) {
		unitIndex++;
	}

	// Normalize all values to the selected unit
	const normalizedData = usageInKB.map((val) =>
		parseFloat((val / Math.pow(1024, unitIndex)).toFixed(2))
	);

	const convertedData = dataList.map((item, index) => ({
		nested_site_name: item.nested_site_name,
		total_usage: normalizedData[index],
		unitLabel: units[unitIndex],
	}));

	return { data: convertedData };
};


export const getHostIdentifier = (hostUrl) => {
	if (!hostUrl) return '';

	let hostname = '';
	let port = '';
	// Check if input contains protocol (e.g., http://)
	const hasProtocol = /^[a-zA-Z]+:\/\//.test(hostUrl);
	if (hasProtocol) {
		try {
			const url = new URL(hostUrl);
			hostname = url.hostname;
			port = url.port;
		} catch (e) {
			return '';
		}
	} else {
		// No protocol — handle like "localhost:3000" or "192.168.1.1"
		const [hostPart] = hostUrl.split('/'); // take part before any "/"
		const [rawHost, rawPort] = hostPart.split(':');
		hostname = rawHost;
		port = rawPort || '';
	}

	// Return IP or localhost as-is with port
	const isIpOrLocal = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname === 'localhost';
	if (isIpOrLocal) return port ? `${hostname}:${port}` : hostname;

	// For domains — extract part before first dot
	const match = hostname.match(/^([^.]+)/);
	if (!match) return '';

	const identifier = match[1];
	return identifier.charAt(0).toUpperCase() + identifier.slice(1);
};

/**
 * Custom hook that ensures an action runs
 * once on initial mount and once again whenever dependencies change.
 *
 * @param {Array} deps Dependency array that triggers reset when changed
 * @return {Function} Function that returns true if the action should run
 * @param {string} [key='default'] 
 */
export function useOnce(deps = [], key = 'default') {

  const calledOnce = useRef(callTracker.get(key) || false);
  const isMounted = useRef(false);
  const prevDeps = useRef(deps);

  // Track mount status
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      // Persist calledOnce state in Strict Mode
      callTracker.set(key, calledOnce.current);
    };
  }, []);

  // Compare dependencies using JSON.stringify for deep comparison
  const depsChanged = JSON.stringify(prevDeps.current) !== JSON.stringify(deps);

  if (depsChanged && isMounted.current) {
    console.log(`useOnce(${key}): Dependencies changed, resetting calledOnce`, { deps });
    calledOnce.current = false;
    callTracker.set(key, false);
    prevDeps.current = deps;
  }

  const shouldRun = () => {
    if (!isMounted.current) {
      console.log(`useOnce(${key}): Not mounted, skipping execution`);
      return false;
    }
    if (calledOnce.current) {
      console.log(`useOnce(${key}): Already called, skipping execution`);
      return false;
    }
    calledOnce.current = true;
    callTracker.set(key, true);
    console.log(`useOnce(${key}): Allowing execution`);
    return true;
  };

  return shouldRun;
}

/**
 * Robust session stitching with:
 * - Per-radio stitching (wifi0=2.4 GHz, wifi1=5 GHz)
 * - Null sessionStop -> use next session's sessionStart; if none, use `now`
 * - Synthesized start when missing -> use previous segment end or windowStart
 * - Interval clipping to [windowStart, windowEnd] (or [windowStart, now] if no windowEnd)
 * - "Last known session" fallback: if nothing intersects the window, show the most recent
 *   segment ending before windowStart, tail-clipped to windowStart.
 * - Single global Active row (most recent by start) across radios
 *
 * @param {String|Date|null} windowStart - ISO or Date; if provided, used for clipping and fallback
 * @param {String|Date|null} windowEnd - ISO or Date; default: now
 * @param {Array} sessions - Aprecomm clientSessionType array
 * @param {Date} now - default new Date()
 * @param {String} locale - e.g. 'en-US'
 * @return {Array} rows ready for UI (sessionStart/Stop labels, pretty durations, active flag)
*/
export function stitchSessions(
  windowStart = null,
  windowEnd = null,
  sessions,
  now = new Date(),
  locale = 'en-US'
) {
  if (!Array.isArray(sessions) || sessions.length === 0) return [];

  const toMs = (v) => (v instanceof Date ? +v : (v ? Date.parse(v) : NaN));
  const windowStartMs = Number.isFinite(toMs(windowStart)) ? toMs(windowStart) : null;
  const windowEndMs   = Number.isFinite(toMs(windowEnd))   ? toMs(windowEnd)   : +now;

  const fmt = (d) =>
    new Intl.DateTimeFormat(locale, {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    }).format(d);

  const prettyDuration = (ms) => {
    let s = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(s / 86400); s %= 86400;
    const h = Math.floor(s / 3600);  s %= 3600;
    const m = Math.floor(s / 60);    s = s % 60;
    const parts = [];
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    if (s) parts.push(`${s}s`);
    return parts.length ? parts.join(' ') : 'Less than 1 second';
  };

  const bw = (code) => {
    switch (String(code)) {
      case '1': return '20 Mhz';
      case '2': return '40 Mhz';
      case '3': return '80 Mhz';
      case '4': return '160 Mhz';
      default:  return '--';
    }
  };

  const parseDate = (v) => {
    const t = Date.parse(v);
    return Number.isFinite(t) ? new Date(t) : null;
  };

  const labelRadio = (rid) =>
    rid === 'wifi0' ? '2.4 GHz' : (rid === 'wifi1' ? '5 GHz' : '--');

  // --- Bucket by radioId
  const byRadio = sessions.reduce((acc, s) => {
    (acc[s.radioId] ||= []).push(s);
    return acc;
  }, {});

  // --- Build segments per radio, then clip to window
  const stitchedAll = [];

  for (const list of Object.values(byRadio)) {
    // Sort by earliest known timestamp
    list.sort((a, b) => {
      const ak = parseDate(a.sessionStart)?.getTime()
        ?? parseDate(a.sessionStop)?.getTime()
        ?? Number.MAX_SAFE_INTEGER;
      const bk = parseDate(b.sessionStart)?.getTime()
        ?? parseDate(b.sessionStop)?.getTime()
        ?? Number.MAX_SAFE_INTEGER;
      return ak - bk;
    });

    let previousEndTs = null;

    for (let i = 0; i < list.length; i++) {
      const cur  = list[i];
      const next = list[i + 1];

      const startReal = parseDate(cur.sessionStart); // may be null
      const stopReal  = parseDate(cur.sessionStop);  // may be null

      const radioName = labelRadio(cur.radioId);

      // --- SPECIAL CASE (UPDATED):
      // start is null but stop exists -> anchor at windowStart OR previousEnd OR stop-1s
      if (!startReal && stopReal) {
        const rawStartMs = (windowStartMs ?? previousEndTs ?? (stopReal.getTime() - 1000));
        let segStartMs = rawStartMs;
        let segEndMs   = stopReal.getTime();

        // Clip to window
        segStartMs = Math.max(segStartMs, windowStartMs ?? segStartMs);
        segEndMs   = Math.min(segEndMs,   windowEndMs   ?? segEndMs);

        if (segEndMs > segStartMs) {
          stitchedAll.push({
            ...cur,
            radioId: radioName,
            _startTs: segStartMs,
            _endTs: segEndMs,
            _durationMs: segEndMs - segStartMs,
            _startLabel: `Before ${fmt(new Date(segEndMs))}`,
            _provisionalActive: false,
            bandwidth: bw(cur.cBw),
          });
        }
        previousEndTs = +stopReal;
        continue;
      }

      // --- Normal path:
      // end: own stop, else next start, else now
      let end = stopReal ?? (next ? parseDate(next.sessionStart) : null) ?? now;
      // start: real start; else previous end; else 0-duration at end
      const start = startReal ?? (previousEndTs ? new Date(previousEndTs) : end);

      if (end < start) end = new Date(start); // guard inversion

      const synthesizedStart = !startReal;
      const beforeAnchor = startReal
        ? startReal
        : (previousEndTs
            ? new Date(previousEndTs)
            : (windowStartMs ? new Date(windowStartMs) : end));

      // BEFORE clipping bounds
      const segStartMsRaw = synthesizedStart ? +beforeAnchor : +start;
      const segEndMsRaw   = +end;

      // Clip to [windowStart, windowEnd]
      const segStartMs = Math.max(segStartMsRaw, windowStartMs ?? segStartMsRaw);
      const segEndMs   = Math.min(segEndMsRaw,   windowEndMs   ?? segEndMsRaw);

      if (segEndMs <= segStartMs) {
        previousEndTs = +end;
        continue; // nothing remains after clipping
      }

      const needsBeforeLabel =
        !startReal || (windowStartMs != null && (synthesizedStart || +start < windowStartMs));
      const startLabel = needsBeforeLabel
        ? `Before ${fmt(new Date(Math.min(+start, windowStartMs ?? +start)))}`
        : null;

      stitchedAll.push({
        ...cur,
        radioId: radioName,
        _startTs: segStartMs,
        _endTs: segEndMs,
        _durationMs: segEndMs - segStartMs,
        _startLabel: startLabel,
        _provisionalActive: !cur.sessionStop && !next, // last on this radio
        bandwidth: bw(cur.cBw),
      });

      previousEndTs = +end;
    }
  }

  // --- If nothing intersects the window, apply "last known session" fallback
  if (!stitchedAll.length && windowStartMs != null) {
    for (const [rid, list] of Object.entries(byRadio)) {
      list.sort((a, b) => {
        const ak = parseDate(a.sessionStart)?.getTime()
          ?? parseDate(a.sessionStop)?.getTime()
          ?? Number.MAX_SAFE_INTEGER;
        const bk = parseDate(b.sessionStart)?.getTime()
          ?? parseDate(b.sessionStop)?.getTime()
          ?? Number.MAX_SAFE_INTEGER;
        return ak - bk;
      });

      let prevEnd = null;
      for (let i = 0; i < list.length; i++) {
        const cur = list[i];
        const next = list[i + 1];

        const startRealMs = toMs(cur.sessionStart);
        const stopRealMs  = toMs(cur.sessionStop);

        const endMs = Number.isFinite(stopRealMs) ? stopRealMs
                    : (next && Number.isFinite(toMs(next.sessionStart))) ? toMs(next.sessionStart)
                    : +now;
        const startMs = Number.isFinite(startRealMs) ? startRealMs : (prevEnd ?? endMs);

        if (endMs <= windowStartMs) {
          const radioName = labelRadio(rid);
          const segStartMs = Math.min(startMs, windowStartMs);
          const segEndMs   = windowStartMs;
          if (segEndMs > segStartMs) {
            stitchedAll.push({
              ...cur,
              radioId: radioName,
              _startTs: segStartMs,
              _endTs: segEndMs,
              _durationMs: segEndMs - segStartMs,
              _startLabel: `Before ${fmt(new Date(windowStartMs))}`,
              _provisionalActive: false,
              bandwidth: bw(cur.cBw),
            });
          }
        }
        prevEnd = endMs;
      }
    }
  }

  // --- Choose ONE global active (most recent by start)
  const candidates = stitchedAll.filter(r => r._provisionalActive);
  let activeKey = null;
  if (candidates.length) {
    const winner = candidates.reduce((a, b) => (a._startTs >= b._startTs ? a : b));
    activeKey = `${winner.radioId}|${winner._startTs}`;
  }

  // --- Finalize for UI
  const rows = stitchedAll.map(r => {
    const isActive = activeKey === `${r.radioId}|${r._startTs}`;
    const startLabel = r._startLabel || fmt(new Date(r._startTs));
    return {
      ...r,
      sessionStart: startLabel,
      sessionStop: isActive ? 'Active' : fmt(new Date(r._endTs)),
      sessionDuration: prettyDuration(r._durationMs),
      active: isActive,
    };
  });

  rows.sort((a, b) => a._startTs - b._startTs);
  return rows;
}
export const formatAlarmsDateWithTimezone = (date) => {
	if (!date || date === '-') return ' ';
	const dateObj = new Date(date);
	const day = String(dateObj.getDate()).padStart(2, '0');
	const month = dateObj.toLocaleString('en-GB', { month: 'short' });
	const year = dateObj.getFullYear();
	const hours = String(dateObj.getHours()).padStart(2, '0');
	const minutes = String(dateObj.getMinutes()).padStart(2, '0');
	const seconds = String(dateObj.getSeconds()).padStart(2, '0');
	return `${day}-${month}-${year}, ${hours}:${minutes}:${seconds}`;
};
export const hasOnlyViewPermission = (name) => {
    const hasView = fetchPermission(name, 'view');
    const hasAdd = fetchPermission(name, 'add');
    const hasChange = fetchPermission(name, 'change');
    const hasDelete = fetchPermission(name, 'delete');
    return hasView && !hasAdd && !hasChange && !hasDelete;
};

const ssidBgColor = [
	'#621c8a',
	'#a79cc7',
	'#9166a9',
	'#e8def1',
	'#e4cadd'
]

// "2025-11-16" -> "16 Nov"
const formatSingleDateLabel = (dateStr) => {
  if (!dateStr) return '';

  try {
    const d = parseISO(dateStr.trim());
    return format(d, 'd MMM'); // e.g. "16 Nov"
  } catch (e) {
    // fallback if parse fails
    return dateStr;
  }
};

// "2025-10-20 : 2025-10-26" -> "20 Oct - 26 Oct"
// "2025-11-16" -> "16 Nov"
const formatKeyLabel = (key) => {
  if (!key) return '';

  if (key.includes(' : ')) {
    // range key (30 days)
    const [startRaw, endRaw] = key.split(' : ');
    const start = formatSingleDateLabel(startRaw);
    const end = formatSingleDateLabel(endRaw);
    return `${start} - ${end}`;
  }

  // single day (7 days)
  return formatSingleDateLabel(key);
};

/**
 * 
 * @param {*} raw 
 * @return Normalize ssid load data based on data type
*/

export const normalizeSSIDData = (raw = [], maxBarThickness = 50) => {
	if (!Array.isArray(raw) || raw.length === 0) {
		return { labels: [], datasets: [] };
	}

	// Case 1: flat list – last 4h / today
	if (raw[0]?.ssid) {
		const sorted = [...raw].sort(
			(a, b) => b.total_clients - a.total_clients
		);

		return {
			labels: [''],
			datasets: sorted.map((item, index) => ({
				label: item.ssid,
				data: [item.total_clients],
				backgroundColor: ssidBgColor[index % ssidBgColor.length],
				maxBarThickness,
				minBarLength: 5,
			})),
		};
	}

	// Case 2: grouped by date – 7 days / month
	// raw can be:
	// [ { "2025-11-16": [...], "2025-11-17": [...] }, { "2025-11-18": [...] } ]

	const dateMap = {};
	raw.forEach((obj) => {
		Object.keys(obj).forEach((dateKey) => {
			// skip non-date keys if any
			const value = obj[dateKey];
			if (Array.isArray(value)) {
				dateMap[dateKey] = value || [];
			}
		});
	});

	const rawKeys = Object.keys(dateMap).sort();

	// pretty labels for x-axis
	const labels = rawKeys.map(formatKeyLabel);

	// collect all SSIDs across all keys
	const ssidSet = new Set();
	rawKeys.forEach((key) => {
		dateMap[key].forEach((entry) => {
			if (entry?.ssid) ssidSet.add(entry.ssid);
		});
	});

	const ssids = Array.from(ssidSet);

	// build dataset per SSID (one value per key, 0 if missing)
	const datasets = ssids
		.map((ssid, index) => {
			const data = rawKeys.map((key) => {
				const entry = dateMap[key].find((e) => e.ssid === ssid);
				if (!entry) return null;

				const value = entry.total_clients;
				return value === 0 ? null : value; // no zero bars
			});

			return {
				label: ssid,
				data,
				backgroundColor: ssidBgColor[index % ssidBgColor.length],
				maxBarThickness,
				minBarLength: 5
			};
		})
		// optional: remove SSIDs that are all null (nothing to show anywhere)
		.filter((ds) => ds.data.some((v) => v !== null));

	return { labels, datasets };
};

/**
 * list by keys
 * @param {*} arrayList 
 * @param {*} keys 
 * @return {object}
*/
export function indexByKeys(arrayList = [], keys) {
	return arrayList.reduce((acc, site) => {
		if (!site || site[keys] == null) return acc;
		acc[site[keys]] = site;
		return acc;
	}, {});
}

export const getWirelessLabel = (type) => {
  const value = (type ?? 'UNKNOWN').toString().trim().toUpperCase();

  switch (value) {
    case '11AX':
      return 'Wi-Fi 6';
	case 'AX':
      return 'Wi-Fi 6';
    case '11AC':
      return 'Wi-Fi 5';
    case '11N':
      return 'Wi-Fi 4';
    case 'LEGACY':
      return 'Legacy';
	case '11A' : 
		return 'LEGACY'
	case '11B' : 
		return 'LEGACY'
	case '11G' : 
		return 'LEGACY'
	case '11BE' : 
		return 'Wi-Fi 7'
	case 'BE' : 
		return 'Wi-Fi 7'
    default:
      return 'Unknown';
	}
}

export const hasExactSameElements = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;

  const set2 = new Set(arr2);
  return arr1.every((item) => set2.has(item));
};
